import { defineStore } from 'pinia'
import { useProductStore } from './products'

export const useWishlistStore = defineStore('wishlist', {
  state: () => ({
    wishlistIds: [],
    _products: {},   // plain object ipv Map - werkt correct met SSR/hydration
    loading: false,
    error: null
  }),

  getters: {
    items:       (state) => state.wishlistIds,
    itemCount:   (state) => state.wishlistIds.length,
    isInWishlist:(state) => (productId) => state.wishlistIds.includes(productId),
    wishlistProducts: (state) => Object.values(state._products),
    totalValue:  (state) => {
      return Object.values(state._products)
        .reduce((sum, p) => sum + parseFloat(p.sale_price || p.price || 0), 0)
        .toFixed(2)
    },
    availableCount: (state) => Object.values(state._products)
      .filter(p => p.stock_status !== 'outofstock').length,
  },

  actions: {
    // Initialize wishlist from localStorage
    initWishlist() {
      if (typeof window === 'undefined') return  // SSR guard
      try {
        const stored = localStorage.getItem('plug2charge_wishlist')
        if (stored) {
          const data = JSON.parse(stored)
          this.wishlistIds = Array.isArray(data) ? data : []
        }
      } catch (error) {
        console.error('Error loading wishlist from localStorage:', error)
        this.wishlistIds = []
      }
    },

    // Save wishlist to localStorage
    saveToLocalStorage() {
      if (typeof window === 'undefined') return  // SSR guard
      try {
        localStorage.setItem('plug2charge_wishlist', JSON.stringify(this.wishlistIds))
      } catch (error) {
        console.error('Error saving wishlist to localStorage:', error)
      }
    },

    // Add product to wishlist
    addToWishlist(product) {
      const productId = typeof product === 'object' ? product.id : product
      
      if (!this.wishlistIds.includes(productId)) {
        this.wishlistIds.push(productId)
        
        // Cache product details if we have them
        if (typeof product === 'object') {
          this._products[productId] = product
        }
        
        this.saveToLocalStorage()
        return true
      }
      return false
    },

    // Remove product from wishlist
    removeFromWishlist(productId) {
      const index = this.wishlistIds.indexOf(productId)
      if (index > -1) {
        this.wishlistIds.splice(index, 1)
        delete this._products[productId]
        this.saveToLocalStorage()
        return true
      }
      return false
    },

    // Toggle product in wishlist
    toggleWishlist(product) {
      const productId = typeof product === 'object' ? product.id : product
      
      if (this.isInWishlist(productId)) {
        this.removeFromWishlist(productId)
        return false
      } else {
        this.addToWishlist(product)
        return true
      }
    },

    // Clear entire wishlist
    clearWishlist() {
      this.wishlistIds = []
      this._products = {}
      this.saveToLocalStorage()
    },

    // Fetch full product details for all wishlist items
    async fetchWishlistProducts() {
      if (this.wishlistIds.length === 0) {
        this._products = {}
        return []
      }

      this.loading = true
      this.error = null

      try {
        const productStore = useProductStore()
        const cachedProducts = []
        const missingIds = []

        // Check product store cache first
        for (const id of this.wishlistIds) {
          const cached = productStore.getProductById(id)
          if (cached) {
            this._products[id] = cached
            cachedProducts.push(cached)
          } else {
            missingIds.push(id)
          }
        }

        // Fetch missing products via proxy (geen client-auth - proxy voegt WC-auth toe)
        if (missingIds.length > 0) {
          const response = await fetch(
            `/api/wc/v3/products?include=${missingIds.join(',')}&per_page=${missingIds.length}`
          )
          if (!response.ok) throw new Error('Failed to fetch wishlist products')
          const fetched = await response.json()
          fetched.forEach(p => { this._products[p.id] = p })
        }

        return Object.values(this._products)
      } catch (error) {
        this.error = error.message
        console.error('Error fetching wishlist products:', error)
        return []
      } finally {
        this.loading = false
      }
    },

    // Sync wishlist with product store cache
    syncWithProductStore() {
      const productStore = useProductStore()
      this.wishlistIds.forEach(id => {
        const product = productStore.getProductById(id)
        if (product) this._products[id] = product
      })
    },

    // Remove products that are no longer available
    async cleanupWishlist() {
      const products = await this.fetchWishlistProducts()
      const validIds = products.map(p => p.id)
      
      // Remove IDs that couldn't be fetched (deleted products)
      this.wishlistIds = this.wishlistIds.filter(id => validIds.includes(id))
      this.saveToLocalStorage()
    },

    // Export wishlist (for sharing)
    exportWishlist() {
      return {
        ids: this.wishlistIds,
        products: Object.values(this._products).map(p => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          price: p.price,
          image: p.images?.[0]?.src
        })),
        totalValue: this.totalValue,
        createdAt: new Date().toISOString()
      }
    },

    // Import wishlist (from shared link)
    importWishlist(data) {
      if (!data || !Array.isArray(data.ids)) {
        console.error('Invalid wishlist data')
        return false
      }

      this.wishlistIds = data.ids
      this.saveToLocalStorage()
      
      // Optionally fetch product details
      this.fetchWishlistProducts()
      
      return true
    },

    // Get wishlist summary stats
    getStats() {
      return {
        totalItems: this.itemCount,
        totalValue: this.totalValue,
        availableItems: this.availableCount,
        outOfStockItems: this.itemCount - this.availableCount
      }
    }
  }
})